import {
  Archive,
  DotsThree,
  Funnel,
  Gear,
  MagnifyingGlass,
  NotePencil,
  Plus,
  Trash,
  Tray,
  UserCircle,
} from "@phosphor-icons/react";
import { createFileRoute } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { Button as AriaButton, Dialog as AriaDialog } from "react-aria-components";
import { AdminSpecimens } from "@/components/dev/AdminSpecimens";
import { NotificationSpecimens } from "@/components/dev/NotificationSpecimens";
import { PlanningSpecimens } from "@/components/dev/PlanningSpecimens";
import { TrackingSpecimens } from "@/components/dev/TrackingSpecimens";
import { TranslationSpecimens } from "@/components/dev/TranslationSpecimens";
import { PRIORITY_VALUES, PriorityIcon } from "@/components/domain/PriorityIcon";
import { STATUS_CATEGORIES, StatusIcon, statusCategoryLabel } from "@/components/domain/StatusIcon";
import { TYPE_ICON_NAMES, TypeIcon } from "@/components/domain/TypeIcon";
import { NavCount, NavItem, sidebarRow, sidebarRowActive } from "@/components/shell/NavItem";
import { PageHeader } from "@/components/shell/Page";
import {
  Avatar,
  Badge,
  Button,
  Checkbox,
  ComboBox,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
  EmptyState,
  IconButton,
  InlineError,
  InstanceMark,
  Kbd,
  KeyHint,
  Menu,
  MenuItem,
  MenuSection,
  MenuSeparator,
  MenuTrigger,
  Modal,
  PasswordField,
  Popover,
  RowSkeleton,
  SectionHeader,
  Select,
  Skeleton,
  Spinner,
  Switch,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  TextField,
  Tip,
} from "@/components/ui";
import { cx } from "@/lib/cx";
import { statusAccent } from "@/lib/format";
import { type Density, densityPref, usePref } from "@/lib/prefs";
import { toast } from "@/lib/toast";

export const Route = createFileRoute("/dev/design")({ component: DesignPage });

type Mode = "both" | "light" | "dark";

/* Documentation page: copy here is English by design, product screens use Paraglide messages. */

function DesignPage() {
  const [mode, setMode] = useState<Mode>("both");
  const [density, setDensity] = usePref(densityPref);

  return (
    <div className="min-h-dvh bg-chrome text-fg">
      <header className="sticky top-0 z-30 flex flex-wrap items-end gap-4 border-b border-line bg-chrome px-4 py-3 sm:px-6">
        <div className="mr-auto flex items-center gap-3">
          <InstanceMark name="Design" size="lg" />
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Design system</h1>
            <p className="text-xs text-fg-muted">
              Every component, variant and state. Tokens only, light and dark from one source.
            </p>
          </div>
        </div>
        <Select
          label="Theme"
          className="w-40"
          selectedKey={mode}
          onSelectionChange={(k) => setMode(k as Mode)}
          items={[
            { id: "both", label: "Side by side" },
            { id: "light", label: "Light only" },
            { id: "dark", label: "Dark only" },
          ]}
        />
        <Select
          label="Density"
          className="w-40"
          selectedKey={density}
          onSelectionChange={(k) => setDensity(k as Density)}
          items={[
            { id: "compact", label: "Compact" },
            { id: "comfortable", label: "Comfortable" },
          ]}
        />
      </header>

      <main
        id="main"
        tabIndex={-1}
        className={cx("mx-auto grid gap-4 p-4 sm:p-6", mode === "both" ? "max-w-[1600px] xl:grid-cols-2" : "max-w-3xl")}
      >
        {mode !== "dark" ? (
          <Pane theme="light">
            <Specimens />
          </Pane>
        ) : null}
        {mode !== "light" ? (
          <Pane theme="dark">
            <Specimens />
          </Pane>
        ) : null}
      </main>
      <p className="px-6 pb-6 text-xs text-fg-muted">
        Popovers, menus and dialogs render in the page theme (System, Light or Dark from Settings), not the pane theme.
      </p>
      <ApiNotes />
    </div>
  );
}

/** Contracts other screens build on. Keep in step with the components' JSDoc. */
const API: Array<[string, string, string]> = [
  [
    "Page",
    "components/shell/Page",
    "<Page title icon? meta? actions? toolbar? width='full|default|narrow'>body</Page>. Renders the only h1. Lists and boards use width='full'; settings-like pages the default 960px.",
  ],
  [
    "PageHeader",
    "components/shell/Page",
    "Same header standalone (headingLevel 1 by default). toolbar is a full-width row for layout tabs and the filter bar.",
  ],
  [
    "useBreadcrumbs",
    "components/shell/breadcrumbs",
    "useBreadcrumbs([{ label, to? }]) fills the canvas top bar and document.title. It no longer renders the h1.",
  ],
  [
    "NavItem, NavCount",
    "components/shell/NavItem",
    "<NavItem to icon label count? countTone='accent|muted' collapsed? />. Active state comes from the router (data-status). sidebarRow / sidebarRowActive export the row geometry for tree rows.",
  ],
  [
    "EmptyState",
    "components/ui",
    "title, description, action?, secondary?, hint? (KeyHint nodes), icon?, align='start|center'. Start-aligned in the content column under a PageHeader; center only for whole-page states.",
  ],
  [
    "Button",
    "components/ui",
    "variant primary (monochrome) | secondary | ghost | danger | accent; size sm 24 | md 28 | lg 32. One primary per view. Put a <Kbd tone='inverse'> inside a primary to show its key.",
  ],
  [
    "IconButton",
    "components/ui",
    "aria-label required (also the tooltip). shortcut={['mod','K']} adds keys to the tooltip; iconWeight, tooltipPlacement optional.",
  ],
  [
    "Kbd, KeyHint",
    "components/ui",
    "<Kbd keys={['mod','K']} tone?='inverse' />; 'mod', 'enter', 'up', 'down', 'left', 'right' are mapped. <KeyHint keys>label</KeyHint> for hint rows and footers.",
  ],
  [
    "Avatar",
    "components/ui",
    "name, seed? (user id: picks one of 8 tints), src?, bot?, size xs | sm | md | lg | xl, labelled?. Bots are rounded squares with the robot glyph.",
  ],
  ["InstanceMark", "components/ui", "<InstanceMark name size='sm|md|lg|xl' />. Monochrome monogram tile, decorative."],
  [
    "Menu",
    "components/ui",
    "header? renders static content above the items. MenuItem shows a check in selectionMode menus and a caret inside SubmenuTrigger.",
  ],
  [
    "Commands",
    "lib/commands",
    "create (quick create), space.create, product.create and go.product.<KEY> are real now; import.github stays a stub. Item commands (item.status, item.priority, ...) act on getActiveView(). Run any of them with useRunCommand()(id).",
  ],
  [
    "PropertyPicker",
    "components/domain/PropertyPicker",
    "label, options [{ id, label, description?, icon?, keywords?, shortcut?, section? }], mode 'single' | 'multiple', selected Set, onPick / onToggle, shortcut?, header?. Put it in a Popover; digits pick while the filter is empty.",
  ],
  [
    "openPicker, PickerHost",
    "components/items/PickerHost",
    "openPicker(kind, targets, anchorEl, onClosed?) opens the shared anchored picker for status | priority | assignee | labels | type | parent | milestone | field:<groupId>. Rows mark cells with data-picker-anchor so the popover lands on the property.",
  ],
  [
    "PickerField",
    "components/items/ItemProperties",
    "<PickerField kind item ctx label>value</PickerField>: a property value button with its own picker (detail sidebar). Multi-select sessions offer one undo when closed.",
  ],
  [
    "useRegisterView, getActiveView",
    "components/items/activeView",
    "Lists, boards, the item page and the peek register { kind, root, targets, anchor, move?, open?, peek?, toggleSelect?, createDefaults?, restoreFocus? }. The view holding focus wins.",
  ],
  [
    "ItemsView, ItemList, Board",
    "components/items",
    "ItemsView runs a query over productIds (data/items useItems) and renders ItemList (virtualised treegrid) or Board (GridList per column with React Aria DnD) with the A6 states. Search state lives in the URL: q, group, sort, peek.",
  ],
  [
    "FilterBar",
    "components/items/FilterBar",
    "value, onChange(q), warnings, isSearching?, productIds? (scopes suggestions), onExit?. Complete clauses become chips on space or pick; Backspace at the start edits the last chip; '/' focuses it.",
  ],
  [
    "ItemView, PeekPanel",
    "components/items/ItemView",
    "<ItemView itemRef variant='page' | 'peek' />. The shell renders PeekPanel for ?peek=KEY: second canvas from 1280px, overlay from 768px, full screen below.",
  ],
  [
    "openQuickCreate",
    "components/items/quickCreateStore",
    "openQuickCreate(defaults?) opens quick create; without defaults it merges the registered views' createDefaults (product, parent, column).",
  ],
  [
    "FieldChip, ItemKey, ProgressRing, AvatarStack",
    "components/domain",
    "FieldChip label color? field? (Field name for screen readers). ItemKey value width?. ProgressRing done total label. AvatarStack people max size.",
  ],
  [
    "DateButton",
    "components/ui",
    "value 'YYYY-MM-DD' | null, onChange, label, placeholder. Quiet button that opens a keyboard-operable calendar with Clear.",
  ],
  [
    "Data layer",
    "data/*",
    "useProductItems(productIds), useItems(query, productIds, { sort }), useItemDetail(ref). Writes: updateItems(targets, build, { undoTitle, versioned }), createItem, deleteItems, moveItemToProduct, addComment, saveDescription. Cache writes only via store.applyItemUpsert / applyItemRemoval.",
  ],
  [
    "priorityLabel",
    "components/domain/PriorityIcon",
    "priorityLabel(value) is the one place that turns 0..4 into text ('P0'..'P3', or Urgent..Low via setPriorityStyle('word')). PriorityIcon value={0..4}.",
  ],
  [
    "SubNav",
    "components/shell/SubNav",
    "<SubNav label groups={[{ label?, items: [{ to, label, icon, exact?, count? }] }]} />: secondary navigation inside the canvas (Administration, Settings). A 208px rail from 1024px, one scrolling row below. The page keeps its own h1.",
  ],
  [
    "DataTable, TableSkeleton",
    "components/ui",
    "<DataTable label columns rows onRowAction? sortDescriptor? onSortChange? renderEmptyState? />. Columns are { id, header, cell(row), isRowHeader?, allowsSorting?, align?, className?, hideHeader? }; rows need a string id; Enter or click on a row calls onRowAction (open a Drawer). dataTableStyles exports the classes for hand-built tables. TableSkeleton rows columns is the cold-load state.",
  ],
  [
    "Drawer, DrawerHeader, DrawerBody, DrawerFooter, DrawerSection",
    "components/ui",
    "Right-edge detail sheet (md 480px, lg 600px) over a scrim: <Drawer isOpen onOpenChange size?>{({ close }) => ...}</Drawer>. The header title is the dialog name; focus returns to the row. Drive isOpen from a search param (useDrawerParam in components/admin/shared) so the open record is deep-linkable.",
  ],
  [
    "ConfirmDialog",
    "components/ui",
    "Alert dialog for destructive actions: isOpen, onOpenChange, title, description, confirmLabel, tone='danger|primary', onConfirm (may throw), describeError?, children? (extra content). Focus starts on Cancel; errors stay inline and the dialog stays open.",
  ],
  [
    "Callout",
    "components/ui",
    "tone info | warning | danger | success, title?, children, actions?, role? ('status' or 'alert' when it appears after an action). In-flow notice; the icon and text carry the meaning, not only the tone.",
  ],
  [
    "CopyField, CopyButton, copyText",
    "components/ui",
    "<CopyField label value description? multiline? rows? />: a read-only, focusable, selectable mono input with a Copy button (label turns into 'Copied', announced politely). For links, commands, redirect URIs and one-time secrets. copyText falls back to execCommand outside secure contexts.",
  ],
  [
    "SearchField",
    "components/ui",
    "label (accessible name), placeholder, value, onChange. Filter box with magnifier and clear button; Escape clears.",
  ],
  [
    "FormSection",
    "components/ui",
    "title, description?, wide?, actions? (badge next to the title), children. Two-column settings section used by Settings and the admin configuration pages.",
  ],
  [
    "segmentTrack, segmentItem, segmentItemActive, segmentItemSelected",
    "components/ui/segmented",
    "Class strings for every segmented control (layout switch, inbox filter, Segmented in display options, login language). Track uses --segment-track, the selected item --segment-active; links set segmentItemActive, ToggleButtons segmentItemSelected.",
  ],
  [
    "SectionHeader",
    "components/ui",
    "id? (for aria-labelledby), title, description?, icon?, actions?, level=2|3. Heading of a stacked section on a wide page (tables, lists): actions line up with the last text line. Use it instead of hand-built h2 + p pairs.",
  ],
  [
    "RadioGroup, Radio",
    "components/ui",
    "<RadioGroup label labelHidden? value onChange><Radio value label description? preview? /></RadioGroup>. Choice cards: the selected card gets the accent border; preview renders a live specimen inside the card.",
  ],
  [
    "StatTile, Progress, RelativeTime",
    "components/ui",
    "StatTile label value (undefined = skeleton) hint? icon? to?. Progress label value? max valueText (no value = indeterminate shimmer). RelativeTime value (ISO) absolute?: a <time> with the exact moment in the tooltip.",
  ],
  [
    "CodeField, QrCode",
    "components/ui",
    "CodeField label value onChange kind='totp|backup': one-time code input (numeric keypad, one-time-code autofill, wide tracking). QrCode value label size?: inline SVG, always dark on light for scanners (uses the uqr package).",
  ],
  [
    "PermissionPicker",
    "components/admin/PermissionPicker",
    "label, value (Permission[]), onChange, readOnly?, allowed?: the permission catalogue as grouped checkboxes with descriptions and an instance-only badge. Used by the role editor and token creation. Names and descriptions live in components/admin/permissions (PERMISSION_TEXT, PERMISSION_GROUPS).",
  ],
  [
    "Inbox, InboxRowView, BulkBar",
    "components/notifications",
    "Inbox filter renders the grouped keyboard list (ARIA grid, roving row: J/K, Enter, Space peek, E archive, U read, X select, Shift+I all read). Row copy is built in copy.tsx from kind + params with slots for the actor, key and status (sentenceOf, fillSentence): never concatenate translated fragments. api.ts holds the queries under ['notifications'] (the live stream invalidates that prefix) and optimistic useNotificationAction.",
  ],
  [
    "NotificationSettingsForm",
    "components/notifications",
    "Per-kind in-app and email switches in one table (the row names the event, the column the channel), the email schedule as Radio cards, and what 'following' means. Every change saves at once and rolls back on error.",
  ],
  [
    "WatchButton, BacklinksSection",
    "components/items",
    "WatchButton itemId compact?: Follow / Following / Muted in the item header with a tooltip (watcher count). BacklinksSection itemId catalog: 'Mentioned in', hidden while empty.",
  ],
  [
    "Mentions",
    "components/items/mentions",
    "MarkdownEditor mentions={{ productId }} adds @ (people) and # (items) suggestions: MentionSuggest is a ProseMirror plugin, MentionController drives the keyboard and the ARIA combobox attributes on the editor, MentionPopup is the listbox (rendered beside the editor, not in a portal, so it stays inside open dialogs). MarkdownView turns @handle into MentionChip and KEY-123 into ItemRef (link + hover card) in prose only; mentionText.tokenize mirrors the server's parser.",
  ],
  [
    "DataTable rows with a `key` field",
    "components/ui/DataTable",
    "React Aria reads `key` of a row object as its identity. Rows that carry an item key (TrashEntry, ItemLite) must be wrapped ({ id, entry }) or the table throws 'Cannot change the id of an item'.",
  ],
  [
    "Webhooks",
    "components/webhooks",
    "WebhookDialog (create or edit, event checklist), SecretDialog (signing secret, shown once), DeliveriesDrawer (settings, test, rotate, pause, delete, delivery log with request and response, redeliver). api.ts has the queries and webhookError(e).",
  ],
  [
    "Admin data helpers",
    "components/admin/*",
    "api.ts has the REST shapes and queryOptions (usersQuery, rolesQuery, auditQuery...); errors.ts adminError(e) maps API codes to sentences; caps.ts useAdminCaps() derives what a person may open from GET /me/permissions (lib/permissions.ts: useMyPermissions, useCanAdmin); shared.tsx has UserCell, QueryBoundary (skeleton, 403 denied, retry), plural, whereText.",
  ],
  [
    "ProductHeader, useProductPage, ProductFallback",
    "components/planning/ProductHeader",
    "Header of every /p/$productKey page: h1 + key, actions, product sections as tabs (Items, Next up, Milestones with live counts), optional toolbar row; the product menu holds Fields, Manage views and the ROADMAP.md export. useProductPage(key) resolves product, space and name; ProductFallback renders the cold skeleton or 'product not found'.",
  ],
  [
    "View state (URL <-> saved view)",
    "components/views/viewState",
    "ViewSearch keys q, group, sub, sort, cols, subs=off, density, rows, zoom (+ view, peek). viewToSearch(view) / searchToDisplay(search, productKey) convert; isDirty(view, layout, search) and hasCustomState(layout, search) compare normalized state per layout; viewLink(view, key) is where a view opens (built-in Next up opens the queue page).",
  ],
  [
    "ViewsMenu, ViewControls, SaveViewHost, PinnedViews",
    "components/views",
    "ViewsMenu product currentId: searchable popover (Standard, My views, Shared) with pin toggles and rename/share/delete (undo). ViewControls: 'Edited' + Reset + Save / Save as when the loaded view diverges, else 'Save view' for custom state. SaveViewHost (one per page, opened with openSaveView) captures query, layout and display; sharing is gated by view.share. PinnedViews is the sidebar 'Views' section (drag or Alt+Up/Down reorder through PUT /views/pinned/order).",
  ],
  [
    "DisplayOptions, Segmented, pill",
    "components/views/DisplayOptions",
    "DisplayOptions layout search defaultGroup? defaultSort?: list (group, sub-group, sort + direction, sub-items, density, properties), board (columns from built-ins and single-select Fields only), timeline (rows, zoom, sub-items). Segmented is a labelled single-choice ToggleButtonGroup; pill is the toggle style for multi-select chips.",
  ],
  [
    "ItemList tree, columns, density",
    "components/items/ItemList",
    "tree: sub-items nest under a parent in the same group (aria-level, aria-expanded; Left collapses or goes to the parent, Right expands or enters the first child). Group.children renders sub-group headers (level 2). columns: ColumnKey[] (priority, key, type, status, fields, milestone, start, due, estimate, created, updated, assignee). density: compact 32 | comfortable 38 | spacious 46 px rows (coarse pointers stay 48).",
  ],
  [
    "Next up (queue)",
    "components/planning/queue, nextup/NextUpPage",
    "buildQueue(items, catalog) mirrors the server queue (P0, review findings, rank; blocked hidden) so reordering is instant. Only the ranked tier reorders: drag handle, Alt+Up/Down, Alt+Home/End, row menu; rankPatches() re-ranks ties so the visible order is exact. The backlog section pulls items in (first unstarted status, end of the ranking).",
  ],
  [
    "Milestones",
    "components/planning/milestones",
    "MilestoneProgress stats size?: segmented bar by status category plus text (never colour only). milestoneStats(items, catalog), targetPhrase(date) ('in 3 weeks', 'n days overdue'), compareMilestones (open by target date then version). updateMilestone() patches the schema cache optimistically and offers undo; MilestoneDialog creates and edits.",
  ],
  [
    "Timeline",
    "components/planning/timeline",
    "model.ts: integer UTC days, zoom scales (weeks 32 / months 9 / quarters 3.2 px per day), buildRows(mode epics|all|milestone). TimelineCanvas: sticky two-tier header and label column, virtualised rows, roving focus on bars (buttons with full accessible names), Alt+Left/Right moves by a step, Alt+Shift+Left/Right changes the due date, Enter opens SetDatesPopover (RangeCalendar), drops from UnscheduledPanel schedule a week. barColors(tone, kind) is the bar style. Phones get TimelineAgenda.",
  ],
  [
    "Fields editor",
    "components/planning/fields",
    "FieldsEditor target={scope, scopeId, productKey, label, canManage}: table of Fields, values a scope adds to shared Fields, plain tags. FieldDrawer edits names, kind (exclusive_conflict 409 is explained with the count, nothing changes), applies-to and required-for types, values. ValuesEditor: inline en/de names (InlineText commits on blur/Enter), ColorSwatchPicker (label palette as a radio group), description, archive/restore, drag or Alt+Up/Down reorder.",
  ],
];

function ApiNotes() {
  return (
    <section aria-labelledby="api-notes" className="mx-auto max-w-[1600px] px-4 pb-12 sm:px-6">
      <div className="rounded-panel border border-canvas-border bg-canvas p-6 shadow-(--canvas-shadow)">
        <h2 id="api-notes" className="mb-4 text-base font-semibold tracking-snug">
          Component contracts
        </h2>
        <dl className="grid gap-x-8 gap-y-3 text-sm md:grid-cols-[max-content_minmax(0,1fr)]">
          {API.map(([name, from, text]) => (
            <div key={name} className="contents">
              <dt className="font-mono text-xs text-fg">
                {name} <span className="text-fg-subtle">@/{from}</span>
              </dt>
              <dd className="m-0 text-fg-muted">{text}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

function Pane({ theme, children }: { theme: "light" | "dark"; children: ReactNode }) {
  return (
    <section
      data-theme={theme}
      aria-label={`${theme} theme`}
      className="min-w-0 rounded-panel border border-canvas-border bg-canvas p-6 text-fg shadow-(--canvas-shadow)"
    >
      <h2 className="mb-6 text-xs font-medium text-fg-subtle">{theme === "light" ? "Light theme" : "Dark theme"}</h2>
      <div className="flex flex-col gap-10">{children}</div>
    </section>
  );
}

/** Type colour names with a distinct glyph each, for the tint specimen. */
const TYPE_TINTS: Array<[string, string]> = [
  ["gray", "Square"],
  ["red", "Bug"],
  ["orange", "Fire"],
  ["yellow", "Lightbulb"],
  ["green", "CheckSquare"],
  ["teal", "Compass"],
  ["blue", "Stack"],
  ["purple", "Rocket"],
  ["pink", "Heart"],
];

function Block({ title, children, className }: { title: string; children: ReactNode; className?: string }) {
  return (
    <div>
      <h3 className="mb-4 border-b border-line pb-2 text-base font-semibold tracking-snug text-fg">{title}</h3>
      <div className={cx("flex flex-wrap items-start gap-3", className)}>{children}</div>
    </div>
  );
}

const SWATCHES: Array<[string, string]> = [
  ["base", "bg-chrome"],
  ["canvas", "bg-canvas"],
  ["subtle", "bg-subtle"],
  ["surface", "bg-surface"],
  ["raised", "bg-raised"],
  ["hover", "bg-hover"],
  ["active", "bg-active"],
  ["line", "bg-line"],
  ["line-strong", "bg-line-strong"],
  ["fg", "bg-fg"],
  ["fg-muted", "bg-fg-muted"],
  ["fg-subtle", "bg-fg-subtle"],
  ["primary", "bg-primary"],
  ["primary-hover", "bg-primary-hover"],
  ["nav-hover", "bg-nav-hover"],
  ["nav-active", "bg-nav-active"],
  ["accent", "bg-accent"],
  ["accent-text", "bg-accent-text"],
  ["accent-soft", "bg-accent-soft"],
  ["danger", "bg-danger"],
  ["danger-soft", "bg-danger-soft"],
  ["warning-soft", "bg-warning-soft"],
  ["success-soft", "bg-success-soft"],
  ["status-backlog", "bg-status-backlog"],
  ["status-unstarted", "bg-status-unstarted"],
  ["status-started", "bg-status-started"],
  ["status-completed", "bg-status-completed"],
  ["status-canceled", "bg-status-canceled"],
  ["priority-urgent", "bg-priority-urgent"],
  ["priority-high", "bg-priority-high"],
  ["priority-medium", "bg-priority-medium"],
];

const AVATAR_NAMES = [
  "Ada Lovelace",
  "Grace Hopper",
  "Linus Torvalds",
  "Margaret Hamilton",
  "Ken Thompson",
  "Barbara Liskov",
  "Dennis Ritchie",
  "Frances Allen",
  "Edsger Dijkstra",
  "Radia Perlman",
];

const SELECT_ITEMS = [
  { id: "en", label: "English" },
  { id: "de", label: "Deutsch" },
  { id: "fr", label: "A deliberately long option label that must truncate instead of breaking the layout" },
];

function Specimens() {
  const [checked, setChecked] = useState(true);
  const [on, setOn] = useState(true);
  const [pending, setPending] = useState(false);

  return (
    <>
      <Block title="Colour tokens" className="gap-2">
        {SWATCHES.map(([name, cls]) => (
          <div key={name} className="flex w-24 flex-col gap-1">
            <span className={cx("h-8 rounded-control border border-line", cls)} />
            <span className="truncate-title font-mono text-2xs text-fg-muted" title={name}>
              {name}
            </span>
          </div>
        ))}
      </Block>

      <Block title="Surfaces: base, canvas, raised" className="flex-col">
        <p className="text-sm text-fg-muted">
          The app chrome sits on the recessed base tone; pages sit on an inset canvas (8px gutter, 10px radius,
          hairline, soft shadow in light). Popovers and dialogs are raised. Peek panels will be a second canvas.
        </p>
        <div className="flex h-56 w-full gap-2 overflow-hidden rounded-panel border border-line bg-chrome p-2 pl-0">
          <div className="flex w-40 shrink-0 flex-col gap-px px-2 pt-1">
            <div className="mb-2 flex h-8 items-center gap-2 px-1.5">
              <InstanceMark name="Acme" />
              <span className="text-sm font-semibold">Acme</span>
            </div>
            <span data-status="active" className={cx(sidebarRow, sidebarRowActive)}>
              <UserCircle size={16} weight="fill" aria-hidden="true" className="text-nav-icon-active" />
              My work
            </span>
            <span className={sidebarRow}>
              <Tray size={16} aria-hidden="true" className="text-fg-subtle" />
              Inbox
              <NavCount value={4} tone="accent" />
            </span>
            <span className={sidebarRow}>
              <Funnel size={16} aria-hidden="true" className="text-fg-subtle" />
              Triage
              <NavCount value={12} />
            </span>
          </div>
          <div className="relative min-w-0 flex-1 rounded-panel border border-canvas-border bg-canvas shadow-(--canvas-shadow)">
            <div className="flex h-9 items-center border-b border-line px-3 text-xs font-medium">My work</div>
            <div className="absolute top-12 right-3 w-36 rounded-panel border border-popover-border bg-raised p-1 shadow-lg">
              <div className="flex h-7 items-center rounded-control bg-active px-2 text-xs">raised</div>
              <div className="flex h-7 items-center px-2 text-xs text-fg-muted">popover</div>
            </div>
          </div>
        </div>
      </Block>

      <Block title="Typography: Geist, weights 450 / 500 / 600" className="flex-col gap-2">
        <p className="text-2xl font-semibold tracking-tight">2xl 20/28 600: auth titles</p>
        <p className="text-xl font-semibold tracking-tight">xl 18/26 600: page title (h1)</p>
        <p className="text-lg font-semibold tracking-snug">lg 16/24 600: dialog titles</p>
        <p className="text-md">md 15/22 450: palette input</p>
        <p className="text-base font-semibold tracking-snug">base 14/20 600: section and empty state titles</p>
        <p className="text-base">base 14/20 450: palette rows, comfortable body</p>
        <p className="text-sm">sm 13/20 450: body, the compact default for lists and forms</p>
        <p className="text-sm font-medium text-fg-muted">sm 13/20 500 muted: nav rows, labels</p>
        <p className="text-xs text-fg-muted">xs 12/16 450 muted: captions, meta, field descriptions</p>
        <p className="text-2xs font-medium text-fg-subtle">2xs 11/16 500 subtle: group labels, counts</p>
        <p className="font-mono text-sm tnum">PLZ-123 PLZ-124 0123456789 (Geist Mono, tabular figures)</p>
      </Block>

      <Block title="Buttons: monochrome primary, the accent is a signal">
        <p className="w-full text-sm text-fg-muted">
          One primary per view. secondary for most actions, ghost in toolbars, danger only to confirm destruction,
          accent rarely and never next to a primary. Sizes sm 24, md 28 (default), lg 32 (auth forms). Press nudges
          0.5px down.
        </p>
        <div className="flex w-full flex-wrap items-center gap-2">
          <Button variant="primary" size="lg">
            Create item
            <Kbd keys={["C"]} tone="inverse" className="-mr-1 ml-1" />
          </Button>
          <Button variant="secondary" size="lg">
            Import from GitHub
          </Button>
          <Button variant="accent">Connect</Button>
        </div>
        {(["primary", "secondary", "ghost", "danger"] as const).map((v) => (
          <div key={v} className="flex flex-col items-start gap-2">
            <Button variant={v}>{v}</Button>
            <Button variant={v} size="sm">
              {v} sm
            </Button>
            <Button variant={v} isDisabled>
              disabled
            </Button>
            <Button variant={v} isPending>
              pending
            </Button>
          </div>
        ))}
        <div className="flex flex-col items-start gap-2">
          <Button
            variant="primary"
            onPress={() => {
              setPending(true);
              setTimeout(() => setPending(false), 1200);
            }}
            isPending={pending}
          >
            <Plus size={14} aria-hidden="true" />
            With icon
          </Button>
          <Button variant="secondary">A noticeably longer button label (German-length)</Button>
          <Spinner className="text-fg-muted" />
        </div>
      </Block>

      <Block title="Icon buttons (aria-label required, tooltip from label)">
        <IconButton aria-label="Search" icon={MagnifyingGlass} shortcut={["mod", "K"]} />
        <IconButton aria-label="New item" icon={NotePencil} variant="secondary" size="sm" shortcut={["C"]} />
        <IconButton aria-label="Settings" icon={Gear} />
        <IconButton aria-label="Settings small" icon={Gear} size="sm" />
        <IconButton aria-label="Add" icon={Plus} variant="secondary" />
        <IconButton aria-label="Delete" icon={Trash} variant="danger" />
        <IconButton aria-label="Disabled" icon={Archive} isDisabled />
        <MenuTrigger>
          <IconButton aria-label="More actions" icon={DotsThree} />
          <Menu aria-label="More actions" onAction={(k) => toast.show({ title: `Action: ${String(k)}` })}>
            <MenuSection title="Item">
              <MenuItem id="open" icon={UserCircle} shortcut={["Enter"]}>
                Open
              </MenuItem>
              <MenuItem id="archive" icon={Archive} shortcut={["E"]}>
                Archive
              </MenuItem>
              <MenuItem id="disabled" isDisabled icon={Tray}>
                Move to inbox (disabled)
              </MenuItem>
            </MenuSection>
            <MenuSeparator />
            <MenuItem id="delete" icon={Trash} tone="danger">
              Move to trash
            </MenuItem>
          </Menu>
        </MenuTrigger>
      </Block>

      <Block title="Text fields" className="grid gap-4 sm:grid-cols-2">
        <TextField label="Default" placeholder="Placeholder example" />
        <TextField label="With description" description="Shown below the input, always visible." defaultValue="Value" />
        <TextField label="Error" error="This field is required." defaultValue="" />
        <TextField label="Disabled" isDisabled defaultValue="Disabled value" />
        <TextField label="Multiline" multiline defaultValue="Several lines of text" className="sm:col-span-2" />
        <PasswordField label="Password" autoComplete="new-password" defaultValue="hunter2hunter2" />
        <PasswordField label="Password with error" error="Use at least 12 characters." />
      </Block>

      <Block title="Select and ComboBox" className="grid gap-4 sm:grid-cols-2">
        <Select label="Select" items={SELECT_ITEMS} defaultSelectedKey="en" />
        <Select label="Select with placeholder" items={SELECT_ITEMS} placeholder="Choose one" />
        <Select label="Select error" items={SELECT_ITEMS} error="Pick a language." />
        <ComboBox label="ComboBox" items={SELECT_ITEMS} placeholder="Type to filter" />
      </Block>

      <Block title="Checkbox, Switch">
        <div className="flex flex-col gap-3">
          <Checkbox isSelected={checked} onChange={setChecked}>
            Checked option
          </Checkbox>
          <Checkbox>Unchecked option</Checkbox>
          <Checkbox isIndeterminate>Indeterminate</Checkbox>
          <Checkbox isDisabled>Disabled</Checkbox>
          <Checkbox description="Helper text sits under the label.">With description</Checkbox>
        </div>
        <div className="flex flex-col gap-3">
          <Switch isSelected={on} onChange={setOn}>
            Switch on
          </Switch>
          <Switch>Switch off</Switch>
          <Switch isDisabled>Disabled</Switch>
          <Switch description="Helper text sits under the label.">With description</Switch>
        </div>
      </Block>

      <Block title="Tabs">
        <Tabs className="w-full">
          <TabList aria-label="Example tabs">
            <Tab id="list">List</Tab>
            <Tab id="board">Board</Tab>
            <Tab id="table">Table</Tab>
            <Tab id="gone" isDisabled>
              Disabled
            </Tab>
          </TabList>
          <TabPanel id="list" className="text-sm text-fg-muted">
            List layout panel.
          </TabPanel>
          <TabPanel id="board" className="text-sm text-fg-muted">
            Board layout panel.
          </TabPanel>
          <TabPanel id="table" className="text-sm text-fg-muted">
            Table layout panel.
          </TabPanel>
        </Tabs>
      </Block>

      <Block title="Overlays: tooltip, popover, dialog, toasts">
        <Tip label="Tooltip text, also on keyboard focus">
          <Button variant="secondary">Hover or focus me</Button>
        </Tip>
        <DialogTrigger>
          <Button variant="secondary">
            <MagnifyingGlass size={14} aria-hidden="true" />
            Popover
          </Button>
          <Popover placement="bottom start" className="w-64">
            <AriaDialog aria-label="Example popover" className="p-3 text-sm outline-none">
              Popover with arbitrary content. Escape closes it and focus returns to the trigger.
            </AriaDialog>
          </Popover>
        </DialogTrigger>
        <DialogTrigger>
          <Button variant="secondary">Dialog</Button>
          <Modal>
            <Dialog aria-labelledby="demo-dialog-title">
              {({ close }) => (
                <>
                  <DialogHeader title={<span id="demo-dialog-title">Delete this view?</span>} onClose={close} />
                  <DialogBody>The view moves to the trash and can be restored for 30 days.</DialogBody>
                  <DialogFooter>
                    <Button variant="ghost" onPress={close}>
                      Cancel
                    </Button>
                    <Button variant="danger" onPress={close}>
                      Move to trash
                    </Button>
                  </DialogFooter>
                </>
              )}
            </Dialog>
          </Modal>
        </DialogTrigger>
        <Button variant="secondary" onPress={() => toast.show({ title: "Saved", tone: "success" })}>
          Success toast
        </Button>
        <Button
          variant="secondary"
          onPress={() => toast.undo("Moved 3 items to Done", () => toast.show({ title: "Undone" }), "Undo")}
        >
          Undo toast
        </Button>
        <Button
          variant="secondary"
          onPress={() =>
            toast.show({
              title: "Could not reach the server",
              description: "Retrying in the background.",
              tone: "danger",
            })
          }
        >
          Error toast
        </Button>
      </Block>

      <Block title="Navigation rows (sidebar)">
        <div className="flex w-56 flex-col gap-px rounded-panel bg-chrome p-2">
          <NavItem to="/" icon={UserCircle} label="My work (live link)" />
          <span data-status="active" className={cx(sidebarRow, sidebarRowActive)}>
            <Tray size={16} weight="fill" aria-hidden="true" className="text-nav-icon-active" />
            <span className="flex-1">Active row</span>
            <NavCount value={3} tone="accent" />
          </span>
          <span className={sidebarRow}>
            <Funnel size={16} aria-hidden="true" className="text-fg-subtle" />
            <span className="flex-1">With total</span>
            <NavCount value={128} />
          </span>
        </div>
        <p className="max-w-xs text-xs text-fg-muted">
          NavItem: 16px regular glyph, fill weight in the accent when active, lifted pill. Counts: accent pill for
          unread, muted tabular figure for totals. The collapsed rail shows a dot and moves the label into a tooltip and
          the accessible name.
        </p>
      </Block>

      <Block title="Page header" className="flex-col">
        <div className="w-full rounded-panel border border-line px-6">
          <PageHeader
            headingLevel={2}
            title="Inbox"
            icon={Tray}
            meta="Mentions, assignments and updates on items you follow"
            actions={
              <>
                <Button variant="secondary">Mark all read</Button>
                <Button variant="primary">New item</Button>
              </>
            }
          />
        </div>
      </Block>

      <Block title="Section header (stacked sections on wide pages)" className="flex-col">
        <div className="w-full rounded-panel border border-line p-6">
          <SectionHeader
            level={3}
            title="Linked repositories"
            description="Repositories connected to a product. Commits and pull requests that mention item keys update those items."
            actions={
              <>
                <Switch>Show archived</Switch>
                <Button variant="secondary">
                  <Plus size={14} aria-hidden="true" />
                  Link repository
                </Button>
              </>
            }
          />
        </div>
      </Block>

      <Block title="Instance mark, Kbd, KeyHint" className="items-center">
        <InstanceMark name="Acme" size="sm" />
        <InstanceMark name="Acme" size="md" />
        <InstanceMark name="Acme" size="lg" />
        <InstanceMark name="Ölwerk" size="xl" />
        <Kbd keys={["mod", "K"]} />
        <Kbd keys={["G", "I"]} />
        <Kbd keys={["up", "down"]} />
        <Kbd keys={["enter"]} />
        <KeyHint keys={["mod", "K"]}>Command palette</KeyHint>
      </Block>

      <Block title="Avatars: 8 hashed tints, bots are squares, xs and sm show one initial" className="flex-col">
        <div className="flex flex-wrap items-center gap-2">
          {AVATAR_NAMES.map((n) => (
            <Avatar key={n} name={n} size="lg" />
          ))}
        </div>
        {(["xs", "sm", "md"] as const).map((size) => (
          <div key={size} className="flex flex-wrap items-center gap-2">
            <span className="w-8 text-xs text-fg-subtle">{size}</span>
            {AVATAR_NAMES.map((n) => (
              <Avatar key={n} name={n} size={size} />
            ))}
          </div>
        ))}
        <div className="flex items-center gap-2">
          <Avatar name="Ada Lovelace" size="xs" />
          <Avatar name="Ada Lovelace" size="sm" />
          <Avatar name="Ada Lovelace" size="md" />
          <Avatar name="Ada Lovelace" size="lg" />
          <Avatar name="Ada Lovelace" size="xl" />
          <Avatar name="Claude" bot size="sm" />
          <Avatar name="Claude" bot size="md" />
          <Avatar name="Claude" bot size="lg" />
          <Avatar name="Claude" bot size="xl" />
        </div>
      </Block>

      <Block title="Badge">
        <Badge>Neutral</Badge>
        <Badge tone="accent">Accent</Badge>
        <Badge tone="danger">Danger</Badge>
        <Badge tone="warning">Warning</Badge>
        <Badge tone="success">Success</Badge>
        <Badge numeric>128</Badge>
      </Block>

      <Block title="Status (shape differs per category)" className="gap-5">
        {STATUS_CATEGORIES.map((c) => (
          <span key={c} className="inline-flex items-center gap-2 text-sm">
            <StatusIcon category={c} decorative size={16} />
            {statusCategoryLabel(c)}
          </span>
        ))}
      </Block>

      <Block title="Priority (bars or glyph plus text)" className="gap-5">
        {PRIORITY_VALUES.map((p) => (
          <PriorityIcon key={p} value={p} showLabel size={16} className="text-sm" />
        ))}
      </Block>

      <Block title="Item type icons (rendered by name)" className="gap-1.5">
        {TYPE_ICON_NAMES.map((n) => (
          <Tip key={n} label={n}>
            <AriaButton
              aria-label={n}
              className="flex size-7 items-center justify-center rounded-control border border-line bg-surface"
            >
              <TypeIcon name={n} decorative size={16} />
            </AriaButton>
          </Tip>
        ))}
      </Block>

      <Block
        title="Type colours (stored type colour tints the glyph, shape stays the signal; >= 3:1 on every surface)"
        className="flex-col gap-3"
      >
        {[
          ["bg-canvas", "canvas"],
          ["bg-chrome", "base"],
          ["bg-active", "active"],
        ].map(([bg, name]) => (
          <div
            key={name}
            className={cx("flex flex-wrap items-center gap-4 rounded-control border border-line p-3", bg)}
          >
            <span className="w-14 text-xs text-fg-muted">{name}</span>
            {TYPE_TINTS.map(([color, icon]) => (
              <span key={color} className="inline-flex items-center gap-1.5 text-xs text-fg">
                <TypeIcon name={icon} color={color} label={color} size={16} />
                {color}
              </span>
            ))}
          </div>
        ))}
      </Block>

      <Block
        title="Status accents (2px column top border and chip leading bar; glyph stays coloured by category)"
        className="gap-3"
      >
        {[...TYPE_TINTS.map(([c]) => c), "slate"].map((color, i) => (
          <div key={color} className="flex w-44 flex-col gap-2">
            <div
              className="rounded-b-control border-t-2 border-transparent bg-subtle px-2 py-1.5 text-xs font-medium text-fg"
              style={{ borderTopColor: statusAccent(color) }}
            >
              {color}
            </div>
            <span className="relative inline-flex min-h-7 items-center gap-2 rounded-control px-2 text-sm text-fg">
              <span
                aria-hidden="true"
                className="absolute inset-y-1.5 left-0 w-0.5 rounded-full"
                style={{ backgroundColor: statusAccent(color) }}
              />
              <StatusIcon category={STATUS_CATEGORIES[i % STATUS_CATEGORIES.length]!} decorative size={16} />
              {statusCategoryLabel(STATUS_CATEGORIES[i % STATUS_CATEGORIES.length]!)}
            </span>
          </div>
        ))}
      </Block>

      <TrackingSpecimens />

      <AdminSpecimens />

      <NotificationSpecimens />

      <PlanningSpecimens />

      <TranslationSpecimens />

      <Block title="Loading, empty and error states" className="flex-col">
        <div className="w-full overflow-hidden rounded-panel border border-line bg-surface">
          <RowSkeleton rows={4} />
        </div>
        <Skeleton className="h-3 w-40" />
        <div className="w-full rounded-panel border border-line p-6">
          <EmptyState
            title="Nothing assigned to you"
            description="Items assigned to you will show up here, ordered by what to work on next."
            action={
              <Button variant="primary">
                Create item
                <Kbd keys={["C"]} tone="inverse" className="-mr-1 ml-1" />
              </Button>
            }
            secondary={<Button variant="secondary">Import from GitHub</Button>}
            hint={<KeyHint keys={["mod", "K"]}>Command palette</KeyHint>}
          />
        </div>
        <div className="w-full rounded-panel border border-line">
          <EmptyState
            align="center"
            icon={Tray}
            title="Page not found"
            description="align=center is only for whole-page states without a page header."
          />
        </div>
        <InlineError
          className="w-full"
          message="Something went wrong. Please try again."
          onRetry={() => toast.show({ title: "Retrying" })}
        />
      </Block>
    </>
  );
}
